import type { CategoriesData } from '../types';

export function buildColorSynonymMap(catData: CategoriesData | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const c of catData?.colors ?? []) {
    map.set(c.label_da.toLowerCase(), c.label_da);
    for (const syn of c.synonyms) {
      map.set(syn.toLowerCase(), c.label_da);
    }
  }
  return map;
}

export function buildCategorySynonymMap(catData: CategoriesData | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const c of catData?.categories ?? []) {
    map.set(c.label_da.toLowerCase(), c.id);
    for (const syn of c.synonyms) {
      map.set(syn.toLowerCase(), c.id);
    }
  }
  return map;
}

export function buildCanonicalBrands(catData: CategoriesData | undefined): string[] {
  return Object.values(catData?.brands ?? {}).flat();
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[] = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[n];
}

/**
 * Fuzzy-match a single token against all category synonyms.
 * Returns the best matching category ID, or null.
 * Priority: exact > prefix (handles Danish plurals) > levenshtein.
 */
function fuzzyMatchCategory(token: string, categoryBySynonym: Map<string, string>): string | null {
  if (token.length < 4) return null;

  // Pass 1: exact (already handled by caller, but also catches it here)
  if (categoryBySynonym.has(token)) return categoryBySynonym.get(token)!;

  let bestCat: string | null = null;
  let bestScore = Infinity; // lower is better

  for (const [syn, catId] of categoryBySynonym) {
    if (syn.length < 4) continue;

    // Prefix match: one is a prefix of the other (handles singular/plural, "jakke"/"jakker")
    if (token.startsWith(syn) || syn.startsWith(token)) {
      const score = Math.abs(syn.length - token.length); // prefer closest length
      if (score < bestScore) { bestScore = score; bestCat = catId; }
      continue;
    }

    // Levenshtein fallback for short edit distances
    const maxLen = Math.max(syn.length, token.length);
    const allowedDist = maxLen <= 6 ? 1 : 2;
    if (Math.abs(syn.length - token.length) <= allowedDist) {
      const dist = levenshtein(syn, token);
      if (dist <= allowedDist && dist + 10 < bestScore) { // +10 to prefer prefix over lev
        bestScore = dist + 10;
        bestCat = catId;
      }
    }
  }

  return bestCat;
}

export function detectFromText(
  text: string,
  colorBySynonym: Map<string, string>,
  canonicalBrands: string[],
  categoryBySynonym: Map<string, string>
): { detectedColor: string | null; detectedBrand: string | null; detectedCategory: string | null } {
  const words = text.trim().toLowerCase().split(/\s+/).filter(Boolean);
  let detectedColor: string | null = null;
  let detectedBrand: string | null = null;
  let detectedCategory: string | null = null;

  // Also try bigrams for multi-word synonyms
  const bigrams = words.slice(0, -1).map((w, i) => `${w} ${words[i + 1]}`);
  const tokens = [...bigrams, ...words]; // bigrams first — more specific

  for (const token of tokens) {
    if (!detectedColor && colorBySynonym.has(token)) {
      detectedColor = colorBySynonym.get(token)!;
    }
    if (!detectedBrand) {
      const match = canonicalBrands.find((b) => b.toLowerCase() === token);
      if (match) detectedBrand = match;
    }
    if (!detectedCategory) {
      detectedCategory = fuzzyMatchCategory(token, categoryBySynonym);
    }
    if (detectedColor && detectedBrand && detectedCategory) break;
  }
  return { detectedColor, detectedBrand, detectedCategory };
}

/** @deprecated Use detectFromText instead */
export function detectColorAndBrand(
  text: string,
  colorBySynonym: Map<string, string>,
  canonicalBrands: string[]
): { detectedColor: string | null; detectedBrand: string | null } {
  const { detectedColor, detectedBrand } = detectFromText(text, colorBySynonym, canonicalBrands, new Map());
  return { detectedColor, detectedBrand };
}
